/**
 * Per-employee display chips — the web counterpart of the mobile
 * `EmployeeTags` component, using the same two tags and the same colours so a
 * person reads identically on both surfaces.
 *
 *  • designation — the 1Office BUDGET NAME ("iPro" / "Senior iPro"), indigo.
 *  • notice      — employee is serving notice, rose, with their last working day.
 *
 * Renders NOTHING when neither applies, so it can be dropped into a row
 * unconditionally.
 *
 * ⚠️ Pass `onNotice` explicitly — never infer it from the presence of a tag.
 * The backend tag map contains EVERY roster employee with a boolean, so a
 * truthiness check on the tag object marks the whole project as resigning. A
 * missing tag means "unknown" and must render nothing rather than a placeholder,
 * or a transient roster failure reads as "nobody is on notice".
 */
interface Props {
  designation?: string | null;
  onNotice?: boolean;
  lastWorkingDay?: string | null;
  /** For rows that already show the role in their own column. */
  hideDesignation?: boolean;
  className?: string;
}

const fmtDay = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
};

export default function EmployeeTags({
  designation, onNotice, lastWorkingDay, hideDesignation, className,
}: Props) {
  const showDesignation = !hideDesignation && !!String(designation || '').trim();
  if (!showDesignation && !onNotice) return null;

  const lwd = fmtDay(lastWorkingDay);
  return (
    <span className={`inline-flex flex-wrap items-center gap-1.5 ${className ?? ''}`}>
      {showDesignation && (
        <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-800">
          {designation}
        </span>
      )}
      {onNotice && (
        <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-semibold text-rose-700">
          {lwd ? `Notice · LWD ${lwd}` : 'Notice'}
        </span>
      )}
    </span>
  );
}
